import { useState, useEffect } from 'react';
import { X } from 'lucide-react';
import { supabase } from '../../../lib/supabase';
import { FormData } from '../../../types/transaction';

interface Agency {
  name: string;
  company: string;
}

interface AgenciesFormProps {
  formData: FormData;
  onChange: (data: FormData) => void;
}

export default function AgenciesForm({ formData, onChange }: AgenciesFormProps) {
  const [agencies, setAgencies] = useState<Agency[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadAgencies();
  }, []);

  const loadAgencies = async () => {
    try {
      const { data, error } = await supabase
        .from('agencies')
        .select('name, company')
        .eq('active', true)
        .order('company')
        .order('name');

      if (error) throw error;
      setAgencies(data || []);
    } catch (err) {
      console.error('Error loading agencies:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleAddAgency = () => {
    onChange({
      ...formData,
      agencies: [...formData.agencies, { name: '' }]
    });
  };

  const handleRemoveAgency = (index: number) => {
    const newAgencies = formData.agencies.filter((_, i) => i !== index);
    onChange({
      ...formData,
      agencies: newAgencies
    });
  };

  const getCompanyLabel = (company: string) => {
    switch (company) {
      case 'KI':
        return 'Kermarrec Immobilier';
      case 'KA':
        return 'Kermarrec Atlantique';
      case 'DRYADE':
        return 'Dryade';
      default:
        return company;
    }
  };

  if (loading) {
    return <div>Chargement des agences...</div>;
  }

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-medium text-gray-900">Agences</h3>
        <button
          type="button"
          onClick={handleAddAgency}
          className="text-sm text-blue-600 hover:text-blue-700"
        >
          + Ajouter une agence
        </button>
      </div>

      {formData.agencies.map((agency, index) => (
        <div key={index} className="flex items-center space-x-4">
          <div className="flex-1">
            <select
              value={agency.name}
              onChange={(e) => {
                const newAgencies = [...formData.agencies];
                newAgencies[index] = { name: e.target.value };
                onChange({ ...formData, agencies: newAgencies });
              }}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/50"
              required
            >
              <option value="">Sélectionner une agence</option>
              {Object.entries(
                agencies.reduce((acc, agency) => {
                  if (!acc[agency.company]) {
                    acc[agency.company] = [];
                  }
                  acc[agency.company].push(agency);
                  return acc;
                }, {} as Record<string, Agency[]>)
              ).map(([company, companyAgencies]) => (
                <optgroup key={company} label={getCompanyLabel(company)}>
                  {companyAgencies.map((a) => (
                    <option key={a.name} value={a.name}>
                      {a.name}
                    </option>
                  ))}
                </optgroup>
              ))}
            </select>
          </div>

          {formData.agencies.length > 1 && (
            <button
              type="button"
              onClick={() => handleRemoveAgency(index)}
              className="text-red-600 hover:text-red-700"
            >
              <X size={20} />
            </button>
          )}
        </div>
      ))}
    </div>
  );
}